import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Send, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const POLL_MS = 10_000;

export default function TeamChatPage() {
  const { user, activeClientId, clients } = useAuth();
  const [messages, setMessages] = useState([]);
  const [team, setTeam] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const lastTsRef = useRef(null);

  const activeClient = clients.find((c) => c.client_id === activeClientId) || clients[0];

  // Initial load + team roster
  const fullLoad = async () => {
    try {
      const [{ data: msgs }, { data: t }] = await Promise.all([
        api.get("/messages"),
        api.get("/messages/team"),
      ]);
      setMessages(msgs);
      setTeam(t);
      lastTsRef.current = msgs[msgs.length - 1]?.created_at || null;
      // Mark everything read on entry
      api.post("/messages/mark-read").catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Cheap incremental poll using `?after=`
  const pollNew = async () => {
    if (!lastTsRef.current) return fullLoad();
    try {
      const { data: news } = await api.get("/messages", { params: { after: lastTsRef.current } });
      if (news.length > 0) {
        setMessages((prev) => [...prev, ...news]);
        lastTsRef.current = news[news.length - 1].created_at;
        // Auto-mark read since the user is on the chat page
        api.post("/messages/mark-read").catch(() => {});
      }
    } catch { /* swallow */ }
  };

  useEffect(() => { fullLoad(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeClientId]);

  useEffect(() => {
    const t = setInterval(pollNew, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    const body = draft.trim();
    setDraft("");
    try {
      const { data: msg } = await api.post("/messages", { content: body });
      setMessages((p) => [...p, msg]);
      lastTsRef.current = msg.created_at;
    } catch (e) { setDraft(body); toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const groupedDays = groupByDay(messages);

  return (
    <div className="space-y-4 max-w-4xl flex flex-col h-[calc(100vh-12rem)]" data-testid="team-chat-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-coral" /> Team Chat
          </h1>
          <p className="text-muted-foreground">
            Workers serving {activeClient?.client_name || "this client"} can chat here to swap shifts and share care updates.
          </p>
        </div>
        <Button variant="outline" className="rounded-full font-bold gap-2" onClick={fullLoad} data-testid="chat-refresh-btn">
          Refresh
        </Button>
      </header>

      {/* Team roster */}
      <Card className="rounded-2xl p-3 border-2 flex flex-wrap gap-2 items-center" data-testid="chat-team-strip">
        <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1.5"><Users className="w-3 h-3" /> In this room</span>
        {team.map((p) => (
          <span key={p.email} className={`rounded-full px-2.5 py-1 text-xs font-bold flex items-center gap-1.5 ${
            p.role === "client" ? "bg-client/15 text-client" : "bg-worker/15 text-worker"
          }`} data-testid={`team-member-${p.email}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
            {p.name}
          </span>
        ))}
        {team.length === 0 && <span className="text-xs text-muted-foreground italic">No teammates linked yet.</span>}
      </Card>

      {/* Messages list */}
      <Card ref={listRef} className="rounded-3xl border-2 p-4 flex-1 overflow-y-auto space-y-1" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="h-full flex items-center justify-center text-center text-muted-foreground text-sm">
            <div>
              <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>Be the first to say hi.</p>
            </div>
          </div>
        )}
        {Object.entries(groupedDays).map(([day, msgs]) => (
          <div key={day} className="space-y-1">
            <DaySeparator day={day} />
            {msgs.map((m, i) => {
              const mine = m.sender_user_id === user.id;
              const prev = msgs[i - 1];
              const samePrev = prev && prev.sender_user_id === m.sender_user_id && (new Date(m.created_at) - new Date(prev.created_at)) < 5 * 60_000;
              return <ChatBubble key={m.id} m={m} mine={mine} compact={samePrev} />;
            })}
          </div>
        ))}
      </Card>

      {/* Composer */}
      <Card className="rounded-3xl border-2 p-3 flex items-end gap-2" data-testid="chat-composer">
        <Textarea
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder="Type a message — Enter to send, Shift+Enter for new line"
          className="rounded-2xl resize-none min-h-[48px] max-h-32 flex-1"
          data-testid="chat-input"
        />
        <Button onClick={send} disabled={sending || !draft.trim()}
                className="rounded-full font-bold gap-2 min-h-[48px]"
                style={{ background: "hsl(var(--coral))", color: "white" }}
                data-testid="chat-send-btn">
          <Send className="w-4 h-4" /> Send
        </Button>
      </Card>
    </div>
  );
}

function DaySeparator({ day }) {
  return (
    <div className="flex items-center gap-2 my-3">
      <div className="flex-1 border-t" />
      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{day}</span>
      <div className="flex-1 border-t" />
    </div>
  );
}

function ChatBubble({ m, mine, compact }) {
  const time = new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
      <div className={`max-w-[78%] ${compact ? "mt-0.5" : "mt-2"}`}>
        {!compact && (
          <div className={`text-[11px] font-bold mb-0.5 ${mine ? "text-right" : ""}`}>
            <span className={m.sender_role === "client" ? "text-client" : "text-worker"}>{m.sender_name}</span>
            <span className="text-muted-foreground font-normal ml-1.5">· {time}</span>
          </div>
        )}
        <div className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${
          mine ? "bg-coral text-white rounded-br-sm"
               : m.sender_role === "client"
               ? "bg-client/15 text-foreground rounded-bl-sm border-2 border-client/20"
               : "bg-muted rounded-bl-sm"
        }`}>
          {m.content}
        </div>
      </div>
    </div>
  );
}

function groupByDay(messages) {
  const out = {};
  messages.forEach((m) => {
    const d = new Date(m.created_at);
    const key = d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
    (out[key] ||= []).push(m);
  });
  return out;
}
